import { Box, SxProps, useTheme } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import * as Highcharts from 'highcharts/highstock';
import { PointOptionsObject } from 'highcharts';
import { map } from 'lodash';
import { useElementSize } from 'usehooks-ts';
import ReactDOMServer from 'react-dom/server';
import Tooltip, { TooltipParams } from 'components/chart/tooltip';

const minPointWidth = 170;

const minOnScreen = 4;

export default function Chart({
  serieses,
  sx,
}: {
  serieses: Highcharts.SeriesLineOptions[];
  sx?: SxProps;
}) {
  const theme = useTheme();
  const [chart, setChart] = useState<Highcharts.Chart | null>(null);
  const [ref, { width }] = useElementSize();

  const categories = map(serieses[0].data, (item) => {
    if ((item as PointOptionsObject).name) {
      return String((item as PointOptionsObject).name);
    }

    return '';
  });

  const options = useMemo(() => {
    const totalAmount = serieses[0].data?.length || 0;
    const maxVisibleAmount = Math.floor(width / minPointWidth);

    const itemsCanPlacedOnScreen = Math.min(totalAmount, maxVisibleAmount);
    const itemsOnScreen = Math.max(itemsCanPlacedOnScreen, minOnScreen);
    const isScroll = itemsOnScreen < totalAmount;

    const pointWidth = Math.floor(width / itemsOnScreen);
    const plotLineWidth = pointWidth - 40;
    const lastItemIndex = totalAmount - 1;

    return {
      chart: {
        reflow: true,
        style: {
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.text.primary,
        },
        spacingRight: 0,
        backgroundColor: 'transparent',
      },
      title: {
        style: {
          display: 'none',
        },
      },

      yAxis: {
        title: {
          style: {
            display: 'none',
          },
        },
        lineColor: 'transparent',
        gridLineColor: theme.palette.custom.chartLines,
        offset: 20,
        labels: {
          align: 'left',
          style: {
            color: theme.palette.custom.chartLabels,
          },
        },
      },

      tooltip: {
        crosshairs: false,
        enabled: true,
        useHTML: true,
        outside: false,
        className: 'CustomTooltip',
        backgroundColor: theme.palette.customBackgrounds.chartTooltip,
        borderRadius: 4,
        borderColor: 'transparent',
        padding: 0,
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          let root = this;

          if (this.points) {
            root = this.points[0];
          }

          const custom = root.point.options.custom as TooltipParams;

          return ReactDOMServer.renderToString(
            <Tooltip theme={theme} {...custom}></Tooltip>
          );
        },
      },

      xAxis: {
        categories,
        labels: {
          overflow: 'allow',
          autoRotation: [0],
          useHTML: true,
          formatter: (item) => {
            return `<span style="white-space: ${
              itemsOnScreen === minOnScreen ? 'break-spaces' : 'nowrap'
            };">${item.value}</span>`;
          },
          style: {
            'color': theme.palette.custom.chartLabels,
            'text-align': 'center',
          },
        },
        min: 0,
        max: lastItemIndex,
        scrollbar: {
          enabled: isScroll,
          barBackgroundColor: theme.palette.custom.chartLabels,
          barBorderRadius: 4,
          barBorderWidth: 0,
          buttonArrowColor: 'none',
          buttonBackgroundColor: 'none',
          buttonBorderWidth: 0,
          buttonBorderRadius: 7,
          trackBackgroundColor: theme.palette.customBackgrounds.elevation4,
          trackBorderWidth: 0,
          trackBorderRadius: 4,
          height: 10,
          liveRedraw: true,
        },
        plotLines: map(serieses[0].data, (s, i) => {
          return {
            value: i,
            color: '#6060681A',
            width: plotLineWidth,
          };
        }),
      },

      legend: {
        enabled: false,
      },

      plotOptions: {
        series: {
          pointWidth: pointWidth,
          label: {
            connectorAllowed: false,
          },
        },
      },

      series: serieses,
    } as Highcharts.Options;
  }, [
    theme,
    width,
    serieses,
    theme.palette.text.primary,
    theme.typography.fontFamily,
    categories,
    theme.palette.custom.chartLabels,
    theme.palette.custom.chartLines,
    theme.palette.customBackgrounds.elevation4,
  ]);

  useEffect(() => {
    if (chart) {
      chart.update(options, true, true);
    }
  }, [options, chart]);

  useEffect(() => {
    const chart = Highcharts.chart('chart', options, () => {});

    setChart(chart);
  }, []);

  // Хук, который сдвигает скроллбар в правый край графика по оси X
  useEffect(() => {
    if (!chart) {
      return;
    }

    const lastItemIndex = chart.series[0].data.length - 1;

    // если скроллбара нет, то все элементы видимы
    if (!chart.xAxis[0].options.scrollbar?.enabled) {
      chart.xAxis[0].setExtremes(0, lastItemIndex);
      return;
    }

    // если скроллбар есть, то видно только minOnScreen последних элементов
    chart.xAxis[0].setExtremes(lastItemIndex - minOnScreen, lastItemIndex);
  }, [chart, serieses, width]);

  return (
    <Box
      ref={ref}
      id="chart"
      sx={{ width: 1, flexGrow: 1, minHeight: 320, ...sx }}
    ></Box>
  );
}
